import type { ChakraProps } from '@chakra-ui/react';
import { Image } from '@chakra-ui/react';
import { tokenListFetcher } from '@cronos-app/binding-swap-sdk';
import type { ChainId_L1 } from '@cronos-app/sdk';
import {
  ChainLayer,
  getChainOfLayer,
  NATIVE_TOKEN_ADDRESS,
} from '@cronos-app/sdk';
import { currentWallet } from '@cronos-app/wallet';
import { useQuery } from '@tanstack/react-query';
import { getTokenAddressKey, Token, TOKEN_CONFIG_MAP } from '@ui/models';
import { useMemo } from 'react';

import type { ExtendedTokenInfo } from '../types';
import { useCardLayer } from '../useCardContextStore';

export const useExtendedTokenInfo = (
  tokenAddr: string | undefined,
): ExtendedTokenInfo | undefined => {
  const allTokens = useCardSupportedTokens();
  return useMemo(
    () =>
      tokenAddr
        ? allTokens.find(
            (t) => t.address.toLowerCase() === tokenAddr.toLowerCase(),
          )
        : undefined,
    [allTokens, tokenAddr],
  );
};

export const getCardTargetToken = (
  network: ChainId_L1,
  layer: ChainLayer,
  symbol: string,
) => getHardcodeToTokens(network, layer).find((t) => t.symbol === symbol);

export const useCardTargetTokens = (): ExtendedTokenInfo[] => {
  const layer = useCardLayer();
  const network = currentWallet.useDesiredNetwork();
  return useMemo(() => getHardcodeToTokens(network, layer), [layer, network]);
};

export const useCardSupportedTokens = (): ExtendedTokenInfo[] => {
  const layer = useCardLayer();
  const network = currentWallet.useDesiredNetwork();
  const toTokens = useCardTargetTokens();
  const hardcode = useMemo(
    () => [...toTokens, ...getHardcodeFromTokens(network, layer)],
    [network, layer, toTokens],
  );
  return (
    useQuery({
      queryKey: ['useCardSupportedTokens', network, layer, hardcode],
      queryFn: async () => {
        const list = await tokenListFetcher(+getChainOfLayer(network, layer));
        const tokens = list
          .map((t) => ({
            address: t.address,
            decimals: t.decimals,
            symbol: t.symbol,
            Icon: t.iconUrl
              ? (props: ChakraProps) => (
                  <Image
                    src={t.iconUrl}
                    boxSize="24px"
                    // eslint-disable-next-line @ts-intl/ts-intl/no-raw-text
                    alt={t.symbol}
                    {...props}
                  />
                )
              : undefined,
          }))
          .sort((a, b) => a.symbol.localeCompare(b.symbol));
        return [
          ...hardcode,
          ...tokens.filter(
            (t) =>
              !hardcode.some(
                (tt) => tt.address.toLowerCase() === t.address.toLowerCase(),
              ),
          ),
        ];
      },
      enabled: !!network,
      retry: false,
    }).data ?? toTokens
  );
};

const getHardcodeToTokens = (network: ChainId_L1, layer: ChainLayer) => [
  danger_tokenAdapter(network, Token.USDC, layer, false),
  danger_tokenAdapter(network, Token.ETH, layer, false),
];

const getHardcodeFromTokens = (
  network: ChainId_L1,
  layer: ChainLayer,
): ExtendedTokenInfo[] =>
  layer === ChainLayer.L2
    ? [
        danger_tokenAdapter(network, Token.zkCRO, layer, true),
        danger_tokenAdapter(network, Token.vUSD, layer, false),
        danger_tokenAdapter(network, Token.vETH, layer, false),
      ]
    : [danger_tokenAdapter(network, Token.CRO, layer, true)];

export const MINIMUM_INPUT_BY_TOKEN: Record<string, number> = {
  ETH: 0.0013,
  USDC: 5,
};

export const MINIMUM_OUTPUT_BY_TOKEN: Record<string, number> = {
  ETH: 0.0013,
  USDC: 5,
};

const cache: Record<string, ExtendedTokenInfo | undefined> = {};

export const danger_tokenAdapter = (
  network: ChainId_L1,
  token: Token,
  layer: ChainLayer,
  isNative: boolean,
): ExtendedTokenInfo => {
  const key = `${network}_${token}_${layer}_${isNative}`;
  const item = cache[key] ?? {
    address: isNative
      ? NATIVE_TOKEN_ADDRESS
      : TOKEN_CONFIG_MAP[network][token][getTokenAddressKey(layer)],
    decimals:
      // the decimal of cro config is wrong, should be 18 in cronos/cronoszk
      token === Token.CRO ? 18 : TOKEN_CONFIG_MAP[network][token].decimals,
    symbol: TOKEN_CONFIG_MAP[network][token].symbol,
    Icon: TOKEN_CONFIG_MAP[network][token].Icon,
  };
  return (cache[key] = item);
};
